import { Box, styled, Typography } from '@mui/material';

export const Container = styled(Box)({
  height: '100%',
  display: 'flex',
  overflow: 'hidden',
  flexDirection: 'column',
});

export const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const MetadataRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(3),
}));

export const MetadataItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const MetadataLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const MetadataValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

export const TranscriptContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

export const UtteranceContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  transition: 'all 0.3s ease',
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: isActive ? theme.shadows[2] : 'none',
  border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
}));

export const SpeakerInfo = styled(Box)({
  gap: '4px',
  flexShrink: 0,
  display: 'flex',
  minWidth: '100px',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

export const SpeakerLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  color: color,
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
}));

export const SpeakerTimestamp = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1rem',
  fontFamily: 'monospace',
  color: theme.palette.primary.main,
}));

export const UtteranceContent = styled(Box)({
  flex: 1,
  gap: '8px',
  display: 'flex',
  flexDirection: 'column',
});

export const UtteranceText = styled(Typography)(({ theme }) => ({
  lineHeight: 1.6,
  fontSize: '0.9375rem',
  color: theme.palette.text.primary,
}));

export const UtteranceFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(0.5),
}));

export const TimestampButton = styled('button')(({ theme }) => ({
  fontWeight: 700,
  cursor: 'pointer',
  padding: '6px 12px',
  fontSize: '0.875rem',
  fontFamily: 'monospace',
  transition: 'all 0.2s ease',
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${theme.palette.primary.main}`,

  '&:disabled': {
    opacity: 0.5,
    cursor: 'default',
  },

  '&:hover:not(:disabled)': {
    transform: 'scale(1.05)',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
}));

export const ConfidenceIndicator = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
}));

export const HighlightedText = styled('span')(({ theme }) => ({
  fontWeight: 600,
  padding: '2px 4px',
  borderRadius: '2px',
  color: theme.palette.warning.contrastText,
  backgroundColor: theme.palette.warning.light,
}));

export const LoadingContainer = styled(Box)({
  height: '100%',
  display: 'flex',
  minHeight: '300px',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
});

export const ErrorContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  minHeight: '300px',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(3),
}));

export const EmptyState = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  minHeight: '200px',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(3),
}));

export const StatusContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  minHeight: '300px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
}));
